import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Switch,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Tooltip,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Slide,
  IconButton,
} from '@mui/material';
import { CheckCircle, ArrowForward, Info, Close as CloseIcon } from '@mui/icons-material';

const SlideDownExitDown = React.forwardRef((props: any, ref: any) => (
  <Slide direction={props.in ? 'down' : 'up'} ref={ref} {...props} />
));
import { Link } from 'react-router-dom';

// ── Types ─────────────────────────────────────────────────────────────────────

type PatternKey = 'sporadic' | 'mixed' | 'regular' | 'heavy';

interface PatternDef {
  label: string;
  desc: string;
  ratio: number;
  pct: string;
}

interface Competitor {
  id: string;
  name: string;
  cat: string;
  perUser: number;
  setup: number;
  onboard: string;
  source: string;
}

// ── Data ──────────────────────────────────────────────────────────────────────

const PATTERNS: Record<PatternKey, PatternDef> = {
  sporadic: { label: 'Sporadic', desc: 'Field agents, brokers, reps who check in occasionally', ratio: 0.08, pct: '~8% active at once' },
  mixed:    { label: 'Mixed',    desc: 'Some daily users, many weekly or monthly',             ratio: 0.18, pct: '~18% active at once' },
  regular:  { label: 'Regular',  desc: 'Most members use it daily during business hours',       ratio: 0.32, pct: '~32% active at once' },
  heavy:    { label: 'Heavy',    desc: 'Always-on team, high simultaneous usage',               ratio: 0.55, pct: '~55% active at once' },
};

const COMPETITORS: Competitor[] = [
  { id: 'highspot',     name: 'Highspot',      cat: 'Enterprise Sales Enablement', perUser: 65, setup: 15000, onboard: '4–8 weeks',  source: 'G2 & Vendr mid-market estimates' },
  { id: 'seismic',      name: 'Seismic',       cat: 'Enterprise Sales Enablement', perUser: 45, setup: 25000, onboard: '6–12 weeks', source: 'G2 & Vendr mid-market estimates' },
  { id: 'showpad',      name: 'Showpad',       cat: 'Sales Enablement',            perUser: 55, setup: 10000, onboard: '4–6 weeks',  source: 'Capterra & analyst estimates' },
  { id: 'contentcamel', name: 'Content Camel', cat: 'Sales Content Management',    perUser: 25, setup: 0,     onboard: 'Self-serve', source: 'Published list pricing' },
  { id: 'guru',         name: 'Guru',          cat: 'Knowledge Management',        perUser: 18, setup: 0,     onboard: 'Self-serve', source: 'Published list pricing' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getT1CPlan(concurrent: number): { name: string; monthly: number } {
  if (concurrent <= 1)  return { name: 'Free Plan',   monthly: 0 };
  if (concurrent <= 10) return { name: 'Small Plan',  monthly: 249 };
  if (concurrent <= 25) return { name: 'Medium Plan', monthly: 599 };
  if (concurrent <= 50) return { name: 'Large Plan',  monthly: 1199 };
  let cost = 1199;
  let rem = Math.ceil(concurrent - 50);
  while (rem > 0) {
    if (rem >= 25)      { cost += 399; rem -= 25; }
    else if (rem >= 10) { cost += 179; rem -= 10; }
    else                { cost += 99;  rem -= 5;  }
  }
  return { name: 'Large + Add-ons', monthly: cost };
}

function fmtMoney(n: number): string {
  if (n === 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString()}`;
}

function fmtFull(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const ComparePage: React.FC = () => {
  const theme = useTheme();

  const [teamSize,      setTeamSize]      = useState<number>(75);
  const [pattern,       setPattern]       = useState<PatternKey>('mixed');
  const [multiOrg,      setMultiOrg]      = useState<boolean>(false);
  const [savingsCompare, setSavingsCompare] = useState<string>('highspot');

  const [multiOrgModalOpen,     setMultiOrgModalOpen]     = useState(false);
  const [multiOrgBannerVisible, setMultiOrgBannerVisible] = useState(false);

  const handleMultiOrgChange = (checked: boolean) => {
    setMultiOrg(checked);
    if (checked) {
      setMultiOrgModalOpen(true);
      setMultiOrgBannerVisible(false);
    } else {
      setMultiOrgModalOpen(false);
      setMultiOrgBannerVisible(false);
    }
  };

  const handleDismissModal = () => {
    setMultiOrgModalOpen(false);
    setTimeout(() => setMultiOrgBannerVisible(true), 300);
  };

  // ── SEO ──
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Content Platform Cost Calculator — Compare vs Highspot, Seismic & More | Tier One Content';

    const script = document.createElement('script');
    script.id   = 'compare-page-ld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Content Platform Cost Calculator',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Any',
      description:
        'Interactive calculator comparing content platform costs. ' +
        'Compare Tier One Content vs Highspot, Seismic, Showpad, Content Camel, and Guru ' +
        'using concurrent user pricing vs per-seat pricing.',
      url: 'https://tieronecontent.com/compare',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      featureList: [
        'Concurrent user pricing comparison',
        'Per-seat vs concurrent cost calculator',
        'Highspot vs Tier One Content pricing',
        'Seismic vs Tier One Content pricing',
        '5-year total cost of ownership',
        'Multi-company team cost analysis',
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      document.getElementById('compare-page-ld')?.remove();
    };
  }, []);

  // ── Derived values ──
  const concurrent = useMemo(
    () => Math.max(1, Math.ceil(teamSize * PATTERNS[pattern].ratio)),
    [teamSize, pattern],
  );

  const t1cPlan   = useMemo(() => getT1CPlan(concurrent), [concurrent]);
  const t1cAnnual = t1cPlan.monthly * 12;

  const compAnnuals = useMemo(() =>
    COMPETITORS.map(c => ({
      ...c,
      annual:    c.perUser * teamSize * 12,
      firstYear: c.perUser * teamSize * 12 + c.setup,
    })),
    [teamSize],
  );

  const maxAnnual  = Math.max(t1cAnnual, ...compAnnuals.map(c => c.annual));
  const t1cBarPct  = maxAnnual > 0 ? Math.max(2, (t1cAnnual / maxAnnual) * 100) : 2;

  const barColor = (annual: number): string => {
    const ratio = t1cAnnual > 0 ? annual / t1cAnnual : Infinity;
    if (ratio <= 1.5) return theme.palette.info.light;
    if (ratio <= 3)   return '#F0A850';
    return theme.palette.error.light;
  };

  const savingsComp    = compAnnuals.find(c => c.id === savingsCompare) ?? compAnnuals[0];
  const fiveYearSaving = (savingsComp.annual * 5 + savingsComp.setup) - (t1cAnnual * 5);
  const perPersonMo    = teamSize > 0 ? t1cPlan.monthly / teamSize : 0;

  // ── Render ──
  return (
    <>
      {/* ── Hero ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip label="Interactive Calculator" color="primary" size="small" sx={{ mb: 2 }} />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            How much could you save with Tier One Content?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 580, mx: 'auto' }}>
            Per-seat pricing penalizes teams with large networks. See what concurrent user pricing
            means for your actual cost — compared to every major alternative.
          </Typography>
        </Container>
      </Box>

      {/* ── Calculator ── */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3} alignItems="flex-start">

          {/* ─ Inputs ─ */}
          <Grid item xs={12} md={4}>
            <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: '0.1em', mb: 3, display: 'block' }}
                >
                  Your Team
                </Typography>

                {/* Team size */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Total team members
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Everyone who needs access — including partner or distributor organizations
                  </Typography>
                  <Slider
                    min={5}
                    max={500}
                    step={5}
                    value={teamSize}
                    onChange={(_, v) => setTeamSize(v as number)}
                    valueLabelDisplay="auto"
                    sx={{ mb: 1 }}
                  />
                  <Chip label={`${teamSize} people`} size="small" color="primary" sx={{ fontWeight: 700 }} />
                </Box>

                {/* Usage pattern */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Usage pattern
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    How often are team members typically active at the same time?
                  </Typography>
                  <Grid container spacing={1}>
                    {(Object.entries(PATTERNS) as [PatternKey, PatternDef][]).map(([key, p]) => (
                      <Grid item xs={6} key={key}>
                        <Card
                          onClick={() => setPattern(key)}
                          sx={{
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: pattern === key ? 'primary.main' : 'divider',
                            bgcolor: pattern === key ? `${theme.palette.primary.main}0D` : 'background.paper',
                            transition: 'all 0.15s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: `${theme.palette.primary.main}08`,
                            },
                          }}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                fontSize={12}
                                color={pattern === key ? 'primary.main' : 'text.primary'}
                              >
                                {p.label}
                              </Typography>
                              {pattern === key && <CheckCircle color="primary" sx={{ fontSize: 14 }} />}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3} sx={{ my: 0.5 }}>
                              {p.desc}
                            </Typography>
                            <Typography variant="caption" color="primary.main" fontWeight={600}>
                              {p.pct}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Multi-org toggle */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Multi-company team?
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Team spans multiple organizations
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        e.g. manufacturer + distributors + reps
                      </Typography>
                    </Box>
                    <Switch
                      checked={multiOrg}
                      onChange={e => handleMultiOrgChange(e.target.checked)}
                      color="primary"
                    />
                  </Paper>
                </Box>

                {/* Concurrent estimate */}
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 800, lineHeight: 1, flexShrink: 0 }}
                  >
                    {concurrent}
                  </Typography>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>
                      Estimated peak concurrent users
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                      Tier One Content charges for this — not your total headcount.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* ─ Results ─ */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

              {/* T1C recommendation */}
              <Card
                sx={{
                  border: `2px solid ${theme.palette.success.main}`,
                  bgcolor: `${theme.palette.success.main}0A`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Chip
                        label="✦ Tier One Content"
                        size="small"
                        sx={{ bgcolor: theme.palette.success.main, color: '#fff', fontWeight: 700, mb: 1 }}
                      />
                      <Typography variant="h4" fontWeight={800}>
                        {t1cPlan.name}
                      </Typography>
                      {t1cPlan.monthly === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          1 concurrent user — ideal for small teams (up to ~12 people) with sporadic access
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="h3"
                        fontWeight={800}
                        sx={{ color: theme.palette.success.dark, lineHeight: 1 }}
                      >
                        {t1cPlan.monthly === 0 ? 'Free' : fmtFull(t1cPlan.monthly)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t1cPlan.monthly === 0 ? 'forever' : 'per month'}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={1.5} sx={{ mt: 2 }}>
                    {[
                      { val: t1cAnnual === 0 ? 'Free' : fmtFull(t1cAnnual),               lbl: 'Annual cost' },
                      { val: perPersonMo < 0.01 ? 'Free' : `$${perPersonMo.toFixed(2)}`,  lbl: 'Per person / month' },
                      { val: '$0',                                                           lbl: 'Setup cost' },
                    ].map(m => (
                      <Grid item xs={4} key={m.lbl}>
                        <Box
                          sx={{
                            bgcolor: `${theme.palette.success.main}18`,
                            borderRadius: 2,
                            p: 1.5,
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" fontWeight={800}>{m.val}</Typography>
                          <Typography variant="caption" color="text.secondary" lineHeight={1.3} display="block">
                            {m.lbl}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    component={Link}
                    to="/signup"
                    variant="contained"
                    color="success"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{ mt: 2, fontWeight: 700 }}
                  >
                    Try Free — No Credit Card Required
                  </Button>
                </CardContent>
              </Card>

              {/* Comparison bars */}
              <Card sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: '0.1em', mb: 0.5, display: 'block' }}
                  >
                    Annual Cost Comparison
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2.5 }}>
                    Based on per-seat pricing × your total team size. Startup costs not included.
                  </Typography>

                  {/* T1C bar */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        Tier One Content — {t1cPlan.name}
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {t1cAnnual === 0 ? 'Free' : `${fmtFull(t1cAnnual)}/yr`}
                      </Typography>
                    </Box>
                    <Box sx={{ height: 10, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                      <Box
                        sx={{
                          height: '100%',
                          width: `${t1cBarPct}%`,
                          bgcolor: theme.palette.success.main,
                          borderRadius: 1,
                          transition: 'width 0.5s ease',
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      borderTop: `2px solid ${theme.palette.success.main}50`,
                      my: 1.5,
                    }}
                  />

                  {compAnnuals.map(c => {
                    const barPct     = maxAnnual > 0 ? (c.annual / maxAnnual) * 100 : 0;
                    const savings    = c.annual - t1cAnnual;
                    const savingsPct = t1cAnnual > 0
                      ? Math.round((savings / c.annual) * 100)
                      : c.annual > 0 ? 100 : 0;
                    const isMore = savings > 0;

                    return (
                      <Box key={c.id} sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5,
                            flexWrap: 'wrap',
                            gap: 0.5,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={700}>{c.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.cat}</Typography>
                            <Tooltip title={`Pricing source: ${c.source}`} arrow>
                              <Info sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" fontWeight={700}>{fmtFull(c.annual)}/yr</Typography>
                            <Chip
                              label={isMore ? `${savingsPct}% more expensive` : 'cheaper than T1C'}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                fontWeight: 700,
                                bgcolor: isMore
                                  ? `${theme.palette.error.main}18`
                                  : `${theme.palette.success.main}18`,
                                color: isMore
                                  ? theme.palette.error.main
                                  : theme.palette.success.main,
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ height: 10, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              width: `${barPct}%`,
                              bgcolor: barColor(c.annual),
                              borderRadius: 1,
                              transition: 'width 0.5s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Bottom row */}
              <Grid container spacing={2.5}>

                {/* Startup costs */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700, letterSpacing: '0.1em', mb: 2, display: 'block' }}
                      >
                        Startup &amp; Onboarding Costs
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: theme.palette.success.main, border: 0, pl: 0 }}>
                              Tier One Content
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: theme.palette.success.main, border: 0 }}>
                              $0
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.success.main, border: 0, fontSize: 12 }}>
                              Same day
                            </TableCell>
                          </TableRow>
                          {COMPETITORS.map(c => (
                            <TableRow key={c.id}>
                              <TableCell sx={{ pl: 0, fontSize: 13 }}>{c.name}</TableCell>
                              <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>
                                {c.setup > 0 ? fmtFull(c.setup) : 'None'}
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{c.onboard}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 5-year savings */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: '100%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      border: 'none',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700, letterSpacing: '0.1em', display: 'block', mb: 1.5 }}
                      >
                        Your Savings vs.
                      </Typography>
                      <Select
                        value={savingsCompare}
                        onChange={e => setSavingsCompare(e.target.value)}
                        size="small"
                        sx={{
                          color: '#fff',
                          mb: 2,
                          width: '100%',
                          '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                          '.MuiSvgIcon-root': { color: '#fff' },
                        }}
                      >
                        {COMPETITORS.map(c => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </Select>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
                        5-year total savings
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight={800}
                        sx={{ color: '#7EC8F8', lineHeight: 1, mb: 0.5 }}
                      >
                        {fmtMoney(fiveYearSaving)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        vs. {savingsComp.name} over 5 years
                        {savingsComp.setup > 0 && ` (incl. ${fmtFull(savingsComp.setup)} setup)`}
                      </Typography>
                      <Box
                        sx={{
                          mt: 2,
                          bgcolor: 'rgba(255,255,255,0.08)',
                          borderRadius: 1.5,
                          p: 1.5,
                        }}
                      >
                        {[
                          { label: `${savingsComp.name} (5 yr)`,    val: fmtFull(savingsComp.annual * 5 + savingsComp.setup), accent: false, divider: false },
                          { label: 'Tier One Content (5 yr)',         val: fmtFull(t1cAnnual * 5),                              accent: true,  divider: false },
                          { label: 'Annual saving',                    val: `${fmtFull(savingsComp.annual - t1cAnnual)}/yr`,      accent: true,  divider: true  },
                        ].map(row => (
                          <Box
                            key={row.label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 0.75,
                              pt:  row.divider ? 1   : 0,
                              mt:  row.divider ? 0.75 : 0,
                              borderTop: row.divider ? '1px solid rgba(255,255,255,0.15)' : 'none',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {row.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{ color: row.accent ? '#7EC8F8' : '#fff' }}
                            >
                              {row.val}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Multi-org banner */}
              <Collapse in={multiOrgBannerVisible} timeout={400}>
                <Card
                  sx={{
                    border: `2px solid ${theme.palette.warning.main}`,
                    bgcolor: `${theme.palette.warning.main}0A`,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                      ⚠ Multi-Company Teams — A Hidden Cost in Per-Seat Tools
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      When your team spans multiple organizations (carrier + brokers, manufacturer + dealers),
                      per-seat platforms create extra friction beyond cost:
                    </Typography>
                    {[
                      'Separate contracts and negotiations per organization',
                      'IT and SSO setup required for each company\'s users',
                      'Content siloed by organization — no shared library',
                      'Billing complexity across entities',
                    ].map(item => (
                      <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                        <CheckCircle color="success" sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">{item}</Typography>
                      </Box>
                    ))}
                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: 'text.primary' }}>
                      Tier One Content is built for this. One subscription, unlimited members across any number of companies,
                      one shared content library with role-based access. No extra contracts.
                    </Typography>
                  </CardContent>
                </Card>
              </Collapse>

            </Box>
          </Grid>
        </Grid>

        {/* Disclaimer */}
        <Paper variant="outlined" sx={{ mt: 4, p: 2.5, bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary" lineHeight={1.7}>
            <strong>About competitor pricing:</strong> Highspot, Seismic, and Showpad do not publish list prices.
            Estimates are based on publicly available ranges from G2, Vendr, Capterra, and analyst reports as of early 2026
            and represent typical mid-market contract rates. Actual quotes may vary based on negotiation, contract length,
            and bundle discounts. Content Camel and Guru pricing based on published list rates.
            Tier One Content pricing is exact and current as of March 2026.
            Concurrent user estimates are approximations — your actual peak usage may differ.{' '}
            <Box
              component={Link}
              to="/pricing"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              See exact Tier One Content pricing →
            </Box>
          </Typography>
        </Paper>
      </Container>

      {/* ── Multi-org modal ── */}
      <Dialog
        open={multiOrgModalOpen}
        onClose={handleDismissModal}
        maxWidth="sm"
        fullWidth
        TransitionComponent={SlideDownExitDown}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="subtitle1" fontWeight={700} color="warning.dark">
            ⚠ Multi-Company Teams — A Hidden Cost in Per-Seat Tools
          </Typography>
          <IconButton
            onClick={handleDismissModal}
            size="small"
            sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            When your team spans multiple organizations (carrier + brokers, manufacturer + dealers),
            per-seat platforms create extra friction beyond cost:
          </Typography>
          {[
            'Separate contracts and negotiations per organization',
            'IT and SSO setup required for each company\'s users',
            'Content siloed by organization — no shared library',
            'Billing complexity across entities',
          ].map(item => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <CheckCircle color="success" sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
          <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: 'text.primary' }}>
            Tier One Content is built for this. One subscription, unlimited members across any number of companies,
            one shared content library with role-based access. No extra contracts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismissModal} variant="contained" color="warning">
            Got it — see full details ↓
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Final CTA ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          py: 8,
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, color: '#fff' }}>
            Ready to cut your content platform costs?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, color: 'rgba(255,255,255,0.9)' }}>
            Start free today — no credit card, no setup fees, no contracts.
          </Typography>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            sx={{
              py: 1.5,
              px: 5,
              fontWeight: 700,
              fontSize: '1rem',
              background: '#ffffff',
              color: theme.palette.primary.dark,
              '&:hover': { background: 'rgba(255,255,255,0.92)' },
            }}
          >
            Start Free Forever
          </Button>
          <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>
            Free plan available • Upgrade anytime • Cancel anytime
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default ComparePage;
